// The recommendation engine, ported from the design's DCLogic class.
//
// This is a client-side stand-in for amrec.v1.RecommendService. It runs over
// the fixture catalog so the UI is reviewable now, and — more usefully — it
// pins down the *contract* the server has to satisfy: a weighted blend of
// independently-explainable signals, where every result can say why it
// surfaced. The server-side scorer (backend milestone M7) is expected to
// produce the same shape, at which point this becomes fixture-only.
//
// Keeping the weights and signal keys here, shared with the Connect adapter,
// means the presets below are the same object either way.

import type { Preset, Recommendation, Signal, SignalKey, SortKey, Filters, Weights, Work, Board } from "./types";

export const PRESETS: Preset[] = [
  // Similarity-led, matching pkg/rank: normalizing tag affinity shrank its
  // magnitude and let score outrank it.
  { key: "balanced", name: "Balanced", weights: { tags: 0.45, mood: 0.20, score: 0.12, pop: 0.08, by: 0.10, len: 0.05 } },
  { key: "deep", name: "Deep cuts", weights: { tags: 0.30, mood: 0.20, score: 0.22, obs: 0.20, by: 0.05, len: 0.03 } },
  { key: "crowd", name: "Crowd favourites", weights: { tags: 0.14, mood: 0.06, score: 0.36, pop: 0.38, by: 0.03, len: 0.03 } },
  { key: "vibe", name: "Vibe match", weights: { tags: 0.26, mood: 0.46, score: 0.10, pop: 0.06, by: 0.07, len: 0.05 } },
];

export const SIGNAL_LABEL: Record<SignalKey, string> = {
  tags: "Genre similarity",
  mood: "Mood similarity",
  score: "Site score",
  pop: "Popularity",
  obs: "Obscurity",
  by: "Creator match",
  len: "Length fit",
};

const LENGTHS = ["short", "medium", "long"];

const clamp = (x: number) => Math.max(0, Math.min(1, x));

/**
 * Jaccard-ish overlap, scaled so a couple of shared genres already reads as a
 * strong signal. The `Math.max(3, ...)` floor stops works with one or two
 * tags from scoring a perfect 1.0 on a single coincidental match.
 */
function overlap(a: string[], b: string[]) {
  const shared = a.filter((x) => b.includes(x));
  return { shared, ratio: (shared.length / Math.max(3, new Set([...a, ...b]).size)) * 1.6 };
}

interface RawSignal { v: number; note: string; short?: string }

function signalsFor(item: Work, seed: Work): Record<SignalKey, RawSignal> {
  const g = overlap(item.genres, seed.genres);
  const m = overlap(item.moods, seed.moods);
  const kindWord = item.kind === "anime" ? "studio" : "author";
  return {
    tags: {
      v: clamp(g.ratio),
      short: g.shared.slice(0, 2).join(" + "),
      note: g.shared.length ? `Shares ${g.shared.join(", ")} with ${seed.title}` : `No genre overlap with ${seed.title}`,
    },
    mood: {
      v: clamp(m.ratio),
      short: m.shared.slice(0, 2).join(" + "),
      note: m.shared.length ? `Both read as ${m.shared.join(" + ")}` : `A different register from ${seed.title}`,
    },
    // 6.6–9.4 is the real spread of aggregate scores; normalising over 0–10
    // would squash everything into the top third of the bar.
    score: { v: clamp((item.score - 6.6) / 2.8), note: `Aggregated ${item.score.toFixed(1)} across scraped sources` },
    pop: { v: clamp(item.pop / 100), note: `Popularity index ${item.pop} of 100` },
    obs: {
      v: clamp(1 - item.pop / 100),
      note: item.pop < 50 ? `Under-seen — index ${item.pop}` : `Widely seen already — index ${item.pop}`,
    },
    by: {
      v: item.by === seed.by ? 1 : 0,
      note: item.by === seed.by ? `Same ${kindWord}: ${item.by}` : `Different ${kindWord} (${item.by})`,
    },
    len: {
      v: item.len === seed.len ? 1 : Math.abs(LENGTHS.indexOf(item.len) - LENGTHS.indexOf(seed.len)) === 1 ? 0.5 : 0.2,
      note: `A ${item.len} run — ${item.count}`,
    },
  };
}

/** Weighted blend, normalised by the weights actually in play. */
export function scoreWork(item: Work, seed: Work, w: Weights): { match: number; signals: Signal[] } {
  const raw = signalsFor(item, seed);
  let total = 0;
  let sum = 0;
  const signals: Signal[] = [];
  for (const k of Object.keys(w) as SignalKey[]) {
    const weight = w[k];
    if (weight == null) continue;
    total += weight * raw[k].v;
    sum += weight;
    signals.push({
      key: k, label: SIGNAL_LABEL[k], value: raw[k].v,
      note: raw[k].note, short: raw[k].short, contribution: weight * raw[k].v,
    });
  }
  // Strongest contributor first — the card's one-line "why" takes the top two.
  signals.sort((a, b) => b.contribution - a.contribution);
  return { match: sum ? Math.round((total / sum) * 100) : 0, signals };
}

/** The one-line justification under a card title. */
export function whyShort(signals: Signal[], item: Work, seed: Work): string {
  const top = signals.filter((p) => p.value > 0.15).slice(0, 2);
  if (!top.length) return `A left-field pick — nothing overlaps with ${seed.title}.`;
  return top
    .map((p) => {
      switch (p.key) {
        case "tags":
        case "mood": return p.short ?? "";
        case "score": return `rated ${item.score.toFixed(1)}`;
        case "pop": return `trending at ${item.pop}`;
        case "obs": return `under-seen at ${item.pop}`;
        case "by": return `same ${item.kind === "anime" ? "studio" : "author"}`;
        default: return `${item.len} run`;
      }
    })
    .filter(Boolean)
    .join(" · ");
}

export function rank(
  catalog: Work[], seed: Work, kind: Work["kind"], filters: Filters, weights: Weights, sort: SortKey,
): Recommendation[] {
  const q = filters.query.trim().toLowerCase();
  let list = catalog.filter((c) => c.kind === kind && c.id !== seed.id);

  if (q) {
    list = list.filter((c) =>
      `${c.title} ${c.by} ${c.genres.join(" ")} ${c.moods.join(" ")}`.toLowerCase().includes(q));
  }
  // Genres are ANDed (narrowing), moods are ORed (widening). That asymmetry is
  // from the design and it's the right call: picking three genres means "all
  // of these", picking three moods means "any of these vibes".
  if (filters.genres.length) list = list.filter((c) => filters.genres.every((g) => c.genres.includes(g)));
  if (filters.moods.length) list = list.filter((c) => filters.moods.some((g) => c.moods.includes(g)));
  if (filters.minScore) list = list.filter((c) => c.score >= filters.minScore);
  if (filters.lens.length) list = list.filter((c) => filters.lens.includes(c.len));
  if (filters.statuses.length) list = list.filter((c) => filters.statuses.includes(c.status));

  const scored: Recommendation[] = list.map((c) => ({ ...c, ...scoreWork(c, seed, weights) }));

  switch (sort) {
    case "score": scored.sort((a, b) => b.score - a.score); break;
    case "pop": scored.sort((a, b) => b.pop - a.pop); break;
    case "new": scored.sort((a, b) => b.year - a.year); break;
    default: scored.sort((a, b) => b.match - a.match);
  }
  return scored;
}

export function buildBoards(catalog: Work[], kind: Work["kind"]): Board[] {
  const pool = catalog.filter((c) => c.kind === kind);
  const mk = (name: string, cmp: (a: Work, b: Work) => number, fmt: (c: Work) => string): Board => ({
    name,
    rows: pool.slice().sort(cmp).slice(0, 7).map((c, i) => ({
      rank: "0" + (i + 1), title: c.title, sub: `${c.by} · ${c.year}`, val: fmt(c), id: c.id,
    })),
  });
  return [
    mk("Top rated", (a, b) => b.score - a.score, (c) => c.score.toFixed(1)),
    mk("Most tracked", (a, b) => b.pop - a.pop, (c) => String(c.pop)),
    // Quality times obscurity — the good thing nobody has watched.
    mk("Deep cuts", (a, b) => b.score * (100 - b.pop) - a.score * (100 - a.pop), (c) => c.score.toFixed(1)),
  ];
}

export const initials = (t: string) =>
  t.replace(/[^A-Za-z0-9 ]/g, " ").trim().split(/\s+/).slice(0, 2).map((x) => x[0]).join("").toUpperCase();

export const metaOf = (c: Work) => `${c.by} · ${c.year} · ${c.count} · ${c.score.toFixed(1)}`;
